import { Action, ActionResult } from "./types";
import { UndoIcon, RedoIcon } from "../components/icons";
import { ToolButton } from "../components/ToolButton";
import { t } from "../i18n";
import History, { HistoryEntry } from "../history";
import { ExcalidrawElement } from "../element/types";
import { AppState } from "../types";
import { KEYS } from "../keys";
import { newElementWith } from "../element/mutateElement";
import { fixBindingsAfterDeletion } from "../element/binding";
import { arrayToMap } from "../utils";
import { isWindows } from "../constants";
import { isMindmapElement } from "../element/typeChecks";

let prevElementsId: string | null = null;

const writeData = (
  prevElements: readonly ExcalidrawElement[],
  appState: AppState,
  updater: () => HistoryEntry | null,
): ActionResult => {
  const commitToHistory = false;
  if (
    !appState.multiElement &&
    !appState.resizingElement &&
    !appState.editingElement &&
    !appState.draggingElement
  ) {
    const data = updater();
    if (data === null) {
      return { commitToHistory };
    }

    const prevElementMap = arrayToMap(prevElements);
    const nextElements = data.elements;
    const nextElementMap = arrayToMap(nextElements);

    const deletedElements = prevElements.filter(
      (prevElement) => !nextElementMap.has(prevElement.id),
    );

    const elements = nextElements
        .map((nextElement) => {
          if (isMindmapElement(nextElement)) {
            return newElementWith(
                prevElementMap.get(nextElement.id) || nextElement,
                {},
            );
          }
          return newElementWith(
              prevElementMap.get(nextElement.id) || nextElement,
              nextElement,
          );
        })
        .concat(
            deletedElements.map((prevElement) => {
              if (isMindmapElement(prevElement)) {
                return newElementWith(prevElement, {});
              }
              return newElementWith(prevElement, { isDeleted: true });
            }),
        );
    fixBindingsAfterDeletion(elements, deletedElements);

    /* merge old  20250623
    const elements = nextElements
      .map((nextElement) =>{
            return newElementWith(
                prevElementMap.get(nextElement.id) || nextElement,
                {...nextElement,isDeleted:Global.g_arrayRemovedFromLibraryIds.includes(nextElement.id)},
            )
          }
      )
      .concat(
        deletedElements.map((prevElement) =>
          newElementWith(prevElement, { isDeleted: true }),
        ),
      );
    fixBindingsAfterDeletion(elements, deletedElements);
    const mindmapElement = deletedElements.filter((el) => isMindmapElement(el));

    if (mindmapElement.length) {
      // console.log(prevElementsId);
      // console.log(mindmapElement[0].id);
      if (prevElementsId !== mindmapElement[0].id) {
        prevElementsId = mindmapElement[0].id;
        const event = new KeyboardEvent("keydown", {
          key: KEYS.DELETE,
          code: KEYS.DELETE,
          bubbles: true, // 事件是否冒泡
          cancelable: true, // 事件是否可以被取消
        });
        // @ts-ignore
        event.isActiveNodeRoot = true;
        document
            .getElementsByClassName("excalidraw-container")?.[0]
            ?.dispatchEvent(event);

        return {
          appState: {
            ...appState,

            mindmapOperations: {
              deletingElements: mindmapElement.map((e) => e.id),
              status: "init",
            },
          },
          commitToHistory: false,
        };
      }
      prevElementsId = null;
    }
     */

    return {
      elements,
      appState: { ...appState, ...data.appState },
      commitToHistory,
      syncHistory: true,
    };
  }
  return { commitToHistory };
};

type ActionCreator = (history: History) => Action;

export const createUndoAction: ActionCreator = (history) => ({
  name: "undo",
  trackEvent: { category: "history" },
  perform: (elements, appState) =>
    writeData(elements, appState, () => history.undoOnce()),
  keyTest: (event) =>
    event[KEYS.CTRL_OR_CMD] &&
    event.key.toLowerCase() === KEYS.Z &&
    !event.shiftKey,
  PanelComponent: ({ updateData, data }) => (
    <ToolButton
      id="undo"
      name="btnUndo"
      type="button"
      icon={UndoIcon}
      aria-label={t("buttons.undo")}
      onClick={updateData}
      size={data?.size || "medium"}
    />
  ),
  commitToHistory: () => false,
});

export const createRedoAction: ActionCreator = (history) => ({
  name: "redo",
  trackEvent: { category: "history" },
  perform: (elements, appState) =>
    writeData(elements, appState, () => history.redoOnce()),
  keyTest: (event) =>
    (event[KEYS.CTRL_OR_CMD] &&
      event.shiftKey &&
      event.key.toLowerCase() === KEYS.Z) ||
    (isWindows && event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === KEYS.Y),
  PanelComponent: ({ updateData, data }) => (
    <ToolButton
      id="redo"
      type="button"
      icon={RedoIcon}
      aria-label={t("buttons.redo")}
      onClick={updateData}
      size={data?.size || "medium"}
    />
  ),
  commitToHistory: () => false,
});
